/* /assets/fg-cross-sell.js — FoamGrip Custom Upsell toggle behavior */

(function () {
  'use strict';

  if (customElements.get('fg-cross-sell')) return;

  customElements.define(
    'fg-cross-sell',
    class FgCrossSell extends HTMLElement {
      connectedCallback() {
        this._sectionId = this.dataset.sectionId;

        // Main product form (Broadcast wraps with <product-form> then an inner <form>)
        this._form = document.getElementById('product-form-' + this._sectionId);

        // Price span inside ATC button
        this._priceEl = document
          .getElementById('shopify-section-' + this._sectionId)
          ?.querySelector('[data-product-price]');

        // Product variant JSON for price lookups on variant change
        this._productId = this.dataset.productId;
        this._variantInput = this._form?.querySelector('[name="id"].product-variant-id');

        // Collect interactive controls
        this._toggles = this.querySelectorAll('.fg-cross-sell__toggle');
        this._cartBtns = this.querySelectorAll('.fg-cross-sell__cart-btn');

        // Base price (cents) and compare-at price from data attributes set in Liquid
        this._basePrice = parseInt(this.dataset.basePrice || '0', 10);
        this._baseComparePrice = parseInt(this.dataset.baseComparePrice || '0', 10);

        this._bindEvents();
      }

      disconnectedCallback() {
        document.removeEventListener('theme:product:add', this._onProductAdd);
      }

      _bindEvents() {
        // Toggle switches → update ATC button price
        this._toggles.forEach((toggle) => {
          toggle.addEventListener('change', () => this._recalculate());
        });

        // Cart icon buttons → directly add to cart
        this._cartBtns.forEach((btn) => {
          btn.addEventListener('click', () => this._addSingleToCart(btn));
        });

        // Update base price when variant changes (for multi-variant main products)
        if (this._variantInput) {
          this._variantInput.addEventListener('change', () => this._onVariantChange());
        }

        // Listen for successful main product add, AFTER it is confirmed in Shopify's cart.
        // Using theme:product:add (fires on success) rather than theme:cart:add (fires before
        // the fetch starts) avoids a race condition on first cart session creation.
        this._onProductAdd = (e) => this._onMainProductAdd(e);
        document.addEventListener('theme:product:add', this._onProductAdd);
      }

      // All checked toggles owned by THIS instance (used for per-instance cart icon buttons)
      _checkedToggles() {
        return [...this._toggles].filter((t) => t.checked && !t.disabled);
      }

      // All checked toggles across ALL fg-cross-sell blocks in the section
      _allCheckedToggles() {
        const sectionEl = document.getElementById('shopify-section-' + this._sectionId);
        if (!sectionEl) return this._checkedToggles();
        return [...sectionEl.querySelectorAll('.fg-cross-sell__toggle')].filter(
          (t) => t.checked && !t.disabled
        );
      }

      _recalculate() {
        if (!this._priceEl) return;

        // Sum addons from ALL fg-cross-sell blocks in the section so that two blocks
        // toggled simultaneously never overwrite each other's contribution.
        const checked = this._allCheckedToggles();

        const addonCents = checked.reduce(
          (sum, t) => sum + parseInt(t.dataset.price || '0', 10),
          0
        );

        if (addonCents === 0) {
          this._priceEl.innerHTML = this._buildPriceHTML(
            this._basePrice,
            this._baseComparePrice
          );
          return;
        }

        const total = this._basePrice + addonCents;

        // Compare-at: each addon contributes its compare-at price if it has one,
        // otherwise falls back to its sale price (no saving on that item).
        const addonCompareCents = checked.reduce((sum, t) => {
          const cp = parseInt(t.dataset.comparePrice || '0', 10);
          return sum + (cp > 0 ? cp : parseInt(t.dataset.price || '0', 10));
        }, 0);
        const compareTotal = this._baseComparePrice + addonCompareCents;

        this._priceEl.innerHTML = this._buildPriceHTML(total, compareTotal);
      }

      // Build the inner HTML for [data-product-price], matching Broadcast's own structure:
      // formatted price text + optional <s> for compare-at (only when compare > price)
      _buildPriceHTML(priceCents, compareCents) {
        if (priceCents === 0) {
          return window.Shopify?.formatMoney
            ? window.Shopify.formatMoney(0, window.theme?.moneyFormat)
            : this._simpleMoney(0);
        }

        const priceFormatted = this._formatMoney(priceCents);
        let html = priceFormatted;

        if (compareCents > priceCents) {
          html += ` <s>${this._formatMoney(compareCents)}</s>`;
        }

        return html;
      }

      _formatMoney(cents) {
        return window.theme?.formatMoney
          ? window.theme.formatMoney(cents, window.theme.moneyFormat)
          : this._simpleMoney(cents);
      }

      _simpleMoney(cents) {
        return '€' + (cents / 100).toFixed(2).replace('.', ',');
      }

      _onVariantChange() {
        const jsonEl = document.getElementById('ProductJSON-' + this._productId);
        if (!jsonEl) return;
        try {
          const data = JSON.parse(jsonEl.textContent);
          const variantId = parseInt(this._variantInput.value, 10);
          const variant = data.variants?.find((v) => v.id === variantId);
          if (variant) {
            this._basePrice = variant.price;
            this._baseComparePrice = variant.compare_at_price || 0;
            // Recalculate after Broadcast has updated the DOM with the new variant price
            if (this._allCheckedToggles().length > 0) {
              requestAnimationFrame(() => this._recalculate());
            }
          }
        } catch (_) {}
      }

      async _addSingleToCart(btn) {
        const variantId = btn.dataset.variantId;
        if (!variantId) return;

        const wrap = btn.closest('.fg-cross-sell__item-wrap');
        const errorEl = wrap?.querySelector('.fg-cross-sell__error');

        btn.disabled = true;
        btn.classList.add('fg-cross-sell__cart-btn--loading');
        this._clearError(errorEl);

        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 }),
          });

          if (res.ok) {
            btn.classList.remove('fg-cross-sell__cart-btn--loading');
            btn.classList.add('fg-cross-sell__cart-btn--added');
            document.dispatchEvent(new CustomEvent('theme:cart:update', { bubbles: true }));
          } else {
            btn.disabled = false;
            btn.classList.remove('fg-cross-sell__cart-btn--loading');
            let message = 'Could not add to cart.';
            try {
              const data = await res.json();
              if (data.description) message = data.description;
              else if (data.message) message = data.message;
            } catch (_) {}
            this._showError(errorEl, message);
          }
        } catch (_) {
          btn.disabled = false;
          btn.classList.remove('fg-cross-sell__cart-btn--loading');
          this._showError(errorEl, 'Could not add to cart.');
        }
      }

      _showError(el, message) {
        if (!el) return;
        el.textContent = message;
        el.hidden = false;
        clearTimeout(this._errorTimer);
        this._errorTimer = setTimeout(() => this._clearError(el), 4000);
      }

      _clearError(el) {
        if (!el) return;
        el.hidden = true;
        el.textContent = '';
      }

      _onMainProductAdd(e) {
        // Only handle events that originated from our own section's ATC button
        const sectionEl = document.getElementById('shopify-section-' + this._sectionId);
        const button = e.detail?.button;
        if (!sectionEl || !button || !sectionEl.contains(button)) return;

        // De-duplicate: when two fg-cross-sell blocks exist in the same section, both
        // receive theme:product:add. Only the first one to run should POST the addons.
        // We use a flag on the section element as a shared mutex; it is cleared once the
        // fetch settles so that subsequent ATC clicks work correctly.
        if (sectionEl.dataset.fgAtcHandled === '1') return;
        sectionEl.dataset.fgAtcHandled = '1';

        // Collect ALL checked addons from ALL fg-cross-sell blocks in the section
        const checked = this._allCheckedToggles();

        if (!checked.length) {
          delete sectionEl.dataset.fgAtcHandled;
          return;
        }

        const items = checked.map((t) => ({
          id: parseInt(t.dataset.variantId, 10),
          quantity: 1,
        }));

        fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ items }),
        })
          .then((res) => {
            delete sectionEl.dataset.fgAtcHandled;
            if (res.ok) {
              // Refresh the cart UI after addons are confirmed in cart
              document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
            }
          })
          .catch(() => {
            delete sectionEl.dataset.fgAtcHandled;
          });
      }
    }
  );
})();
